import {
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import {
  selectStayOptionApiV1StaysOptionsTokenSelectPost
} from '../client'
import {
  StayEvents,
  trackEvent
} from '../analytics/posthog'
import {
  API_BASE_URL
} from '../config'
import {
  useStayOptionSet
} from '../hooks/useStayOptionSet'
import {
  atPropertyLabel,
  choiceProperties,
  findChoice,
  formatExactPrice,
  formatStayDates,
  readConfirmParams,
  refundPolicy,
  stayListPath
} from '../utils/stayOptions'
import type {
  ChoiceSource,
  StayChoice
} from '../utils/stayOptions'
import {
  ALREADY_PICKED,
  CENTERED_SCREEN,
  HotelImage,
  OptionSetStatus,
  PageShell
} from './StayPageParts'

// The confirm button ignores taps this long after the page opens. The tap that
// opened it can land where the button now sits, and a quick second tap must
// not book.
const CONFIRM_ARM_MS = 600

const TERMS_URL = '/terms-new'

function Notice({ text, token }: { text: string, token: string }): JSX.Element {
  return (
    <PageShell>
      <div className={CENTERED_SCREEN}>
        <p className="text-gray-600">{text}</p>
        <Link to={stayListPath(token)} className="mt-4 text-sm text-gray-500 underline hover:text-catch-black">
          See all options
        </Link>
      </div>
    </PageShell>
  )
}

/**
 * The page where the owner confirms a stay (route: /stays/:token/confirm).
 *
 * A tap on the options page only opens this page. The assistant is told to book
 * when "Yes, book it" is pressed here, and not before (GET-20286).
 */
export function StayConfirmPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const { offerId, source } = readConfirmParams(searchParams)
  const { state, data, retry, markSelected } = useStayOptionSet(token, 'confirm')
  const [bookedName, setBookedName] = useState<string | null>(null)
  const choice = useMemo(
    () => (data && offerId ? findChoice(data.options, offerId) : null),
    [data, offerId]
  )

  if (state !== 'ready' || !data || !token) {
    return <OptionSetStatus state={state === 'ready' ? 'loading' : state} onRetry={retry} />
  }

  // Before the already-picked check: a successful pick marks the set as taken.
  if (bookedName) {
    return (
      <PageShell>
        <div className={`${CENTERED_SCREEN} animate-fade-in`}>
          <div className="text-5xl">🎉</div>
          <h1 className="mt-4 font-display text-3xl">Nice pick</h1>
          <p className="mt-3 text-gray-600">
            I&apos;ve asked your assistant to book <strong>{bookedName}</strong>.
            Head back to your chat, where it&apos;ll confirm the booking.
          </p>
        </div>
      </PageShell>
    )
  }

  if (data.already_selected) {
    return <Notice text={ALREADY_PICKED} token={token} />
  }

  if (!choice) {
    return <Notice text="That room isn't on this page any more." token={token} />
  }

  return (
    <ConfirmStay
      key={choice.room.offer_id}
      token={token}
      choice={choice}
      source={source}
      stayDates={formatStayDates(data.search_params)}
      onBooked={(name) => {
        setBookedName(name)
        markSelected()
      }}
      onTaken={markSelected}
    />
  )
}

function ConfirmStay({
  token,
  choice,
  source,
  stayDates,
  onBooked,
  onTaken
}: {
  token: string
  choice: StayChoice
  source: ChoiceSource
  stayDates: string | null
  onBooked: (hotelName: string) => void
  onTaken: () => void
}): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const [armed, setArmed] = useState(false)
  const [status, setStatus] = useState<'idle' | 'sending' | 'failed'>('idle')

  useEffect(() => {
    trackEvent(StayEvents.CONFIRM_VIEWED, choiceProperties(choice, source))
    const timer = window.setTimeout(() => setArmed(true), CONFIRM_ARM_MS)
    return () => window.clearTimeout(timer)
  }, [choice, source])

  function leave(leftBy: 'not_now' | 'back_link'): void {
    trackEvent(StayEvents.BOOKING_CANCELLED, {
      ...choiceProperties(choice, source),
      dismissed_by: leftBy
    })
    // Step back when the options page is behind us in history, so the browser's
    // back button doesn't return here; a page opened directly goes to the list.
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate(stayListPath(token), { replace: true })
    }
  }

  async function confirm(): Promise<void> {
    if (!armed || status === 'sending') {
      return
    }
    const properties = choiceProperties(choice, source)
    trackEvent(StayEvents.BOOKING_CONFIRMED, properties)
    setStatus('sending')
    const res = await selectStayOptionApiV1StaysOptionsTokenSelectPost({
      path: { token },
      // The server hands back "book that exact one" only for a confirmed pick.
      body: {
        offer_id: choice.room.offer_id, confirmed: true, source
      },
      baseURL: API_BASE_URL,
      throwOnError: false
    })
    if (!res.error) {
      trackEvent(StayEvents.OPTION_CHOSEN, { ...properties, hotel_name: res.data?.hotel_name })
      onBooked(res.data?.hotel_name ?? choice.option.name)
      return
    }
    // A 409 means a pick was already made from this set.
    if (res.response?.status === 409) {
      trackEvent(StayEvents.OPTION_ALREADY_TAKEN, { offer_id: choice.room.offer_id, source })
      onTaken()
      return
    }
    setStatus('failed')
  }

  const { option, room } = choice
  const roomLine = [room.room_name, room.board_name].filter(Boolean).join(' · ')
  const atProperty = atPropertyLabel(room)
  const sending = status === 'sending'

  return (
    <PageShell>
      {/* Bottom padding keeps the Terms line scrollable above the cookie banner. */}
      <main className="mx-auto max-w-md px-6 pb-40 pt-5">
        <button
          type="button"
          onClick={() => leave('back_link')}
          className="text-sm text-gray-500 underline hover:text-catch-black"
        >
          ← All options
        </button>
        <h1 className="mt-4 font-display text-2xl sm:text-3xl">
          Are you sure you want to book this room?
        </h1>
        {/* A thumbnail, not a full-width photo: on a phone a large image pushes
            "Yes, book it" below the cookie banner. 240 is the width the list
            already loaded, so the image comes from the browser cache. */}
        <div className="mt-5 flex items-center gap-3">
          <HotelImage option={option} width={240} wrapperClass="h-16 w-16 shrink-0 rounded-xl" />
          <p className="font-medium">{option.name}</p>
        </div>
        <dl className="mt-4 space-y-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Room</dt>
            <dd>{roomLine || 'Room'}</dd>
          </div>
          {stayDates && (
            <div>
              <dt className="text-xs text-gray-500">Dates</dt>
              <dd>{stayDates}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-gray-500">Price</dt>
            <dd className="font-semibold tabular-nums">
              {formatExactPrice(room.price, room.currency)} total stay
            </dd>
            {atProperty !== null && (
              <dd className="text-xs text-gray-500 tabular-nums">{atProperty}</dd>
            )}
          </div>
          <div>
            <dt className="text-xs text-gray-500">Cancellation</dt>
            <dd className={room.refundable ? 'text-green-700' : 'font-medium text-catch-red'}>
              {refundPolicy(room)}
            </dd>
          </div>
        </dl>
        <div className="mt-8 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!armed || sending}
            className="rounded-full bg-catch-red px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
          >
            {sending ? 'Sending…' : 'Yes, book it'}
          </button>
          <button
            type="button"
            onClick={() => leave('not_now')}
            disabled={sending}
            className="rounded-full border border-catch-beige-dark px-4 py-3 text-sm font-medium disabled:opacity-40"
          >
            Not now
          </button>
        </div>
        {status === 'failed' && (
          <p role="alert" className="mt-3 text-center text-sm text-catch-red">
            That didn&apos;t go through. Please try again.
          </p>
        )}
        <p className="mt-4 text-center text-xs text-gray-500">
          By booking, you agree to our{' '}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent(StayEvents.TERMS_OPENED, { source })}
            className="underline hover:text-catch-black"
          >
            Terms of Service
          </a>
          .
        </p>
      </main>
    </PageShell>
  )
}
